import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, type ApiProject } from "../lib/api";
import { NewProjectModal } from "../components/NewProjectModal";
import { Skeleton, Spinner } from "../components/Loading";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; projects: ApiProject[] }
  | { status: "error"; message: string };

type ToastState = {
  project: ApiProject;
  // Set to true once Undo has been clicked, so the toast doesn't auto-clear
  // while the unarchive request is still resolving.
  undoing: boolean;
};

const UNDO_TOAST_MS = 6000;

export function DashboardPage() {
  const { state } = useAuth();
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const reload = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const res = await api.listProjects({ includeArchived: showArchived });
      setLoad({ status: "ready", projects: res.projects });
    } catch {
      setLoad({ status: "error", message: "Couldn’t load projects." });
    }
  }, [showArchived]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Auto-dismiss the undo toast after a beat. If the user clicks Undo we
  // flip `undoing` so this timer no longer kills the toast mid-request.
  useEffect(() => {
    if (!toast || toast.undoing) return;
    const id = setTimeout(() => setToast(null), UNDO_TOAST_MS);
    return () => clearTimeout(id);
  }, [toast]);

  const undoArchive = useCallback(async () => {
    if (!toast) return;
    const p = toast.project;
    setToast({ project: p, undoing: true });
    try {
      await api.unarchiveProject(p.id);
      // Drop the archivedAt the optimistic remove had implied, then push
      // back into the list. Sort-stable by createdAt-desc preserves order.
      setLoad((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              projects: [{ ...p, archivedAt: null }, ...prev.projects].sort(
                (a, b) => b.createdAt - a.createdAt,
              ),
            }
          : prev,
      );
      setToast(null);
    } catch {
      setToast({ project: p, undoing: false });
    }
  }, [toast]);

  // Cheap client-side filter — owners typically have at most a few dozen
  // projects, so substring-match on name + targetUrl is plenty. Bumps to
  // a server-side search only become worthwhile in the hundreds.
  const filtered = useMemo(() => {
    if (load.status !== "ready") return load;
    const q = query.trim().toLowerCase();
    if (!q) return load;
    const matches = load.projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.targetUrl.toLowerCase().includes(q),
    );
    return { status: "ready" as const, projects: matches };
  }, [load, query]);

  const greeting =
    state.status === "authed" ? `Welcome back, ${state.user.name}` : "Your projects";

  return (
    <section className="mx-auto max-w-5xl px-6 py-12">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{greeting}</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/users"
            className="text-sm text-neutral-600 hover:text-neutral-900"
          >
            Users
          </Link>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700"
          >
            New project
          </button>
        </div>
      </div>

      {/* Show the search input + archived toggle whenever the list could
          plausibly need them — either there are projects, or the user is
          already viewing the archived set (in which case we keep the
          toggle so they can flip back). */}
      {load.status === "ready" &&
      (load.projects.length > 0 || showArchived) ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-sm">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects by name or URL"
              aria-label="Search projects"
              className="w-full rounded-md border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-neutral-400 focus:border-neutral-900 focus:outline-none"
            />
          </div>
          {query ? (
            <p className="text-xs text-neutral-500">
              {filtered.status === "ready" ? filtered.projects.length : 0} of{" "}
              {load.projects.length}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="ml-auto text-xs text-neutral-600 hover:text-neutral-900"
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      ) : null}

      <div className="mt-4">
        {load.status === "loading" ? (
          <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-center justify-between px-5 py-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="ml-4 h-3 w-20" />
              </li>
            ))}
          </ul>
        ) : load.status === "error" ? (
          <p className="text-sm text-red-600">{load.message}</p>
        ) : load.projects.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 p-12 text-center text-neutral-500">
            <p>No projects yet.</p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-3 text-sm text-neutral-900 underline"
            >
              Create your first one
            </button>
          </div>
        ) : filtered.status === "ready" && filtered.projects.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
            <p className="text-sm">
              No projects match{" "}
              <span className="font-medium text-neutral-700">“{query}”</span>.
            </p>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="mt-3 text-xs text-neutral-900 underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
            {filtered.status === "ready"
              ? filtered.projects.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    onArchive={() => {
                      // Optimistically drop the row when we're not in
                      // "show archived" mode (it'd just clutter the view).
                      // When archived view is on, keep the row but flip
                      // its archivedAt so the UI swaps to "Unarchive".
                      setLoad((prev) => {
                        if (prev.status !== "ready") return prev;
                        if (showArchived) {
                          return {
                            status: "ready",
                            projects: prev.projects.map((x) =>
                              x.id === p.id ? { ...x, archivedAt: Date.now() } : x,
                            ),
                          };
                        }
                        return {
                          status: "ready",
                          projects: prev.projects.filter((x) => x.id !== p.id),
                        };
                      });
                      setToast({ project: p, undoing: false });
                    }}
                    onUnarchive={() => {
                      // Active view: drop the now-active row from the
                      // archived-only set; refetch isn't strictly needed
                      // since the user can toggle back. When in mixed/all
                      // mode (archived shown), just clear archivedAt.
                      setLoad((prev) => {
                        if (prev.status !== "ready") return prev;
                        return {
                          status: "ready",
                          projects: prev.projects.map((x) =>
                            x.id === p.id ? { ...x, archivedAt: null } : x,
                          ),
                        };
                      });
                      // Suppress any stale toast for this same project.
                      setToast((t) =>
                        t && t.project.id === p.id ? null : t,
                      );
                    }}
                  />
                ))
              : null}
          </ul>
        )}
      </div>

      <NewProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(project) => {
          setModalOpen(false);
          setLoad((prev) =>
            prev.status === "ready"
              ? { status: "ready", projects: [project, ...prev.projects] }
              : { status: "ready", projects: [project] },
          );
        }}
      />

      {toast ? (
        <UndoToast
          message={`Archived “${toast.project.name}”.`}
          actionLabel={toast.undoing ? "Restoring…" : "Undo"}
          actionDisabled={toast.undoing}
          onAction={() => void undoArchive()}
          onDismiss={() => setToast(null)}
        />
      ) : null}
    </section>
  );
}

function SearchIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path
        d="M20 20l-3.5-3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ProjectRow({
  project,
  onArchive,
  onUnarchive,
}: {
  project: ApiProject;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const created = new Date(project.createdAt).toLocaleDateString();
  const by =
    project.createdByName?.trim() || project.createdByEmail || null;
  const isArchived = project.archivedAt !== null;

  async function archive() {
    const ok = window.confirm(
      `Archive "${project.name}"? It will be hidden from every Avista owner's dashboard. The share link keeps working until you also delete the project.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.archiveProject(project.id);
      onArchive();
    } catch {
      setError("Couldn’t archive. Try again.");
      setBusy(false);
    }
  }

  async function unarchive() {
    setBusy(true);
    setError(null);
    try {
      await api.unarchiveProject(project.id);
      onUnarchive();
    } catch {
      setError("Couldn’t unarchive. Try again.");
      setBusy(false);
    }
  }

  return (
    <li
      className={
        "group flex items-start justify-between gap-4 px-5 py-4 " +
        (isArchived ? "bg-neutral-50/60" : "")
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/projects/${project.id}`}
            className="font-medium hover:underline"
          >
            {project.name}
          </Link>
          {isArchived ? (
            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] uppercase tracking-wide text-neutral-600">
              archived
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-sm text-neutral-500">
          {project.targetUrl}
        </p>
        <CommentBadges
          open={project.openCommentCount}
          resolved={project.resolvedCommentCount}
        />
        {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2 text-xs text-neutral-500">
        <div className="text-right">
          <div>Created {created}</div>
          {by ? <div className="mt-0.5 text-neutral-400">by {by}</div> : null}
        </div>
        {isArchived ? (
          // Unarchive is a positive action — always-visible, emerald.
          <button
            type="button"
            onClick={() => void unarchive()}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
          >
            {busy ? <Spinner size={12} /> : null}
            Unarchive
          </button>
        ) : (
          // Archive is destructive — hide until row hover/focus, red.
          <button
            type="button"
            onClick={() => void archive()}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs text-red-600 opacity-0 transition-opacity hover:text-red-800 focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-50"
          >
            {busy ? <Spinner size={12} /> : null}
            Archive
          </button>
        )}
      </div>
    </li>
  );
}

function UndoToast({
  message,
  actionLabel,
  actionDisabled,
  onAction,
  onDismiss,
}: {
  message: string;
  actionLabel: string;
  actionDisabled: boolean;
  onAction: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3 rounded-full bg-neutral-900 px-4 py-2 text-sm text-white shadow-lg"
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onAction}
        disabled={actionDisabled}
        className="inline-flex items-center gap-1 font-medium text-amber-300 hover:text-amber-200 disabled:opacity-60"
      >
        {actionDisabled ? <Spinner size={12} /> : null}
        {actionLabel}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-neutral-400 hover:text-white"
      >
        ×
      </button>
    </div>
  );
}

function CommentBadges({
  open,
  resolved,
}: {
  open: number;
  resolved: number;
}) {
  if (open === 0 && resolved === 0) {
    return (
      <p className="mt-2 text-xs text-neutral-400">No comments yet.</p>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[11px]">
      {open > 0 ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
          {open} open
        </span>
      ) : null}
      {resolved > 0 ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {resolved} resolved
        </span>
      ) : null}
    </div>
  );
}
